import { useEffect, useState } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { USER_ROLES, type UserRole } from '../types';

export interface UserProfile {
  user: User | null;
  email: string | null;
  language: string;
  loading: boolean;
  darkMode: boolean;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  /** The single role key from users/{uid}.role — null until granted. */
  role: UserRole | null;
  /** Lender catalog entry this user belongs to (lender_admin only). */
  lenderId?: string;
  status?: 'invited' | 'active' | 'blocked' | 'archive' | 'deactivated';
  /** Persisted per-view order of summary stat cards, keyed by viewId. */
  summaryCardOrder?: { [viewId: string]: string[] };
}

function parseRole(value: unknown): UserRole | null {
  return USER_ROLES.includes(value as UserRole) ? (value as UserRole) : null;
}

export const useUserProfile = (): UserProfile => {
  const [user, setUser] = useState<User | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [language, setLanguage] = useState<string>('en');
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('theme');
    if (saved === 'dark') return true;
    if (saved === 'light') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [avatar, setAvatar] = useState<string>('');
  const [role, setRole] = useState<UserRole | null>(null);
  const [lenderId, setLenderId] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<'invited' | 'active' | 'blocked' | 'archive' | 'deactivated'>('active');
  const [summaryCardOrder, setSummaryCardOrder] = useState<{ [viewId: string]: string[] } | undefined>();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let unsubscribeDoc: (() => void) | undefined;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setEmail(null);
        setLanguage('en');
        setFirstName('');
        setLastName('');
        setAvatar('');
        setRole(null);
        setLenderId(undefined);
        setStatus('active');
        setSummaryCardOrder(undefined);
        setLoading(false);
        return;
      }

      setUser(firebaseUser);
      setEmail(firebaseUser.email ?? null);

      try {
        const userRef = doc(db, 'users', firebaseUser.uid);
        unsubscribeDoc = onSnapshot(
          userRef,
          (snap) => {
            if (snap.exists()) {
              const data = snap.data();

              setLanguage(data.language || 'en');

              if (typeof data.darkMode === 'boolean') {
                setDarkMode(data.darkMode);
                localStorage.setItem('theme', data.darkMode ? 'dark' : 'light');
              }

              setSummaryCardOrder(
                data.summaryCardOrder && typeof data.summaryCardOrder === 'object'
                  ? (data.summaryCardOrder as { [viewId: string]: string[] })
                  : undefined,
              );
              const firstNameValue =
                data.firstName || data.first_name || data.givenName || data.given_name || '';
              const lastNameValue =
                data.lastName || data.last_name || data.familyName || data.family_name || '';

              setFirstName(firstNameValue);
              setLastName(lastNameValue);
              setAvatar(data.avatar || '');
              setRole(parseRole(data.role));
              setLenderId(typeof data.lenderId === 'string' ? data.lenderId : undefined);
              setStatus(data.status || 'active');
            }
          },
          (error) => {
            console.error('Failed to listen to user document:', error);
          }
        );
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
      }

      setLoading(false);
    });

    return () => {
      unsubscribe();
      if (unsubscribeDoc) {
        unsubscribeDoc();
      }
    };
  }, []);

  return {
    user,
    email,
    language,
    loading,
    darkMode,
    firstName,
    lastName,
    avatar,
    role,
    lenderId,
    status,
    summaryCardOrder,
  };
};
